import { ChangeDetectionStrategy, Component, OnInit, Signal, computed, inject, signal } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { Tag } from '../../../core/models/tag.model';
import { parseDefaultTagIds, WorkContext } from '../../../core/models/work-context.model';
import { InteractionTypeService } from '../../../core/services/interaction-type.service';
import { LoggerService } from '../../../core/services/logger.service';
import { TagService } from '../../../core/services/tag.service';
import { TranslationService } from '../../../core/services/translation.service';
import { WorkContextService } from '../../../core/services/work-context.service';
import { BackButtonComponent } from '../../../shared/components/back-button/back-button.component';
import { ButtonComponent } from '../../../shared/components/button/button.component';
import { CardActionsComponent } from '../../../shared/components/card/card-actions.component';
import { CardContentComponent } from '../../../shared/components/card/card-content.component';
import { CardHeaderComponent } from '../../../shared/components/card/card-header.component';
import { CardTitleComponent } from '../../../shared/components/card/card-title.component';
import { CardComponent } from '../../../shared/components/card/card.component';
import { IconComponent } from '../../../shared/components/icon/icon.component';
import { ModalDialogComponent } from '../../../shared/components/modal-dialog/modal-dialog.component';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';

interface WorkContextFormModel {
  name: string;
  description: string;
  tag_ids: string[];
  default_interaction_type: string;
}

/** Admin CRUD for work contexts ("Campaigns", `work-context.manage`) — design doc §6.5. */
@Component({
  selector: 'app-work-contexts-settings',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormlyModule,
    BackButtonComponent,
    ButtonComponent,
    CardComponent,
    CardHeaderComponent,
    CardTitleComponent,
    CardContentComponent,
    CardActionsComponent,
    IconComponent,
    ModalDialogComponent,
    TranslatePipe,
  ],
  templateUrl: './work-contexts-settings.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './work-contexts-settings.component.css',
})
export class WorkContextsSettingsComponent implements OnInit {
  private workContextService = inject(WorkContextService);
  private tagService = inject(TagService);
  private interactionTypeService = inject(InteractionTypeService);
  private translate = inject(TranslationService);
  private log = inject(LoggerService).getLogger('work-contexts-settings');

  contexts!: Signal<WorkContext[]>;
  private tags!: Signal<Tag[]>;
  private tagLabelsById = computed(() => new Map(this.tags().map((tag) => [tag.id, tag.label])));

  showEditor = signal(false);
  editing = signal<WorkContext | null>(null);
  saving = signal(false);
  error = signal<string | null>(null);

  form = new FormGroup({});
  model: WorkContextFormModel = { name: '', description: '', tag_ids: [], default_interaction_type: '' };
  fields: FormlyFieldConfig[] = [];

  async ngOnInit(): Promise<void> {
    this.contexts = this.workContextService.createQuery().data;
    this.tags = this.tagService.createActiveQuery().data;

    // Formly's field options are a one-time snapshot built at init, same as
    // the objection/interaction-type dropdowns elsewhere in this app — the
    // reactive `tags` signal above is only for the list's tag-label display.
    const [tags, types] = await Promise.all([this.tagService.getActive(), this.interactionTypeService.getActive()]);

    const t = (key: string) => this.translate.translate(key);
    this.fields = [
      { key: 'name', type: 'text', props: { label: t('settings.workContexts.form.name'), required: true } },
      { key: 'description', type: 'textarea', props: { label: t('settings.workContexts.form.description') } },
      {
        key: 'default_interaction_type',
        type: 'native-select',
        props: {
          label: t('settings.workContexts.form.defaultType'),
          showLabel: true,
          options: [
            { value: '', label: t('settings.workContexts.form.noDefaultType') },
            ...types.map((type) => ({ value: type.id, label: type.label })),
          ],
        },
      },
      ...(tags.length > 0
        ? [
            {
              key: 'tag_ids',
              type: 'multi-checkbox',
              props: { label: t('settings.workContexts.form.defaultTags'), options: tags.map((tag) => ({ value: tag.id, label: tag.label })) },
            },
          ]
        : []),
    ];
  }

  tagLabelsFor(context: WorkContext): string {
    const labelsById = this.tagLabelsById();
    return parseDefaultTagIds(context)
      .map((id) => labelsById.get(id))
      .filter((label): label is string => !!label)
      .join(', ');
  }

  openCreate(): void {
    this.editing.set(null);
    this.model = { name: '', description: '', tag_ids: [], default_interaction_type: '' };
    this.error.set(null);
    this.showEditor.set(true);
  }

  openEdit(context: WorkContext): void {
    this.editing.set(context);
    this.model = {
      name: context.name,
      description: context.description,
      tag_ids: parseDefaultTagIds(context),
      default_interaction_type: context.default_interaction_type,
    };
    this.error.set(null);
    this.showEditor.set(true);
  }

  closeEditor(): void {
    this.showEditor.set(false);
  }

  async save(): Promise<void> {
    this.saving.set(true);
    this.error.set(null);
    try {
      const existing = this.editing();
      const params = {
        name: this.model.name,
        description: this.model.description,
        default_tag_ids: this.model.tag_ids,
        default_interaction_type: this.model.default_interaction_type,
      };
      if (existing) {
        await this.workContextService.update({ id: existing.id, ...params });
      } else {
        await this.workContextService.create(params);
      }
      this.showEditor.set(false);
    } catch (err) {
      this.log.error('Failed to save work context', err);
      this.error.set(this.translate.translate('settings.workContexts.saveError'));
    } finally {
      this.saving.set(false);
    }
  }

  async toggleActive(context: WorkContext): Promise<void> {
    try {
      await this.workContextService.update({ id: context.id, is_active: context.is_active ? 0 : 1 });
    } catch (err) {
      this.log.error('Failed to toggle work context active state', err);
    }
  }
}
